import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useMemo } from "react";
import type { ReviewGuide } from "../../shared/guide";
import {
  makeReviewEvent,
  projectProgress,
  type LayerStatus,
  type RegionStatus,
  type ReviewEventInput,
  type ReviewVerdict,
} from "../../shared/progress";
import { db, sessionIdFor, type DraftAnchor, type DraftComment, type DraftCommentKind, type ReviewLayerViewState } from "../lib/db";
import { appendHostedReviewEvents } from "../lib/hosted";

export function useReviewSession(fixtureId: string, guide: ReviewGuide, remoteSessionId?: string) {
  const sessionId = sessionIdFor(fixtureId, guide.source.headSha);
  const eventsQuery = useLiveQuery(() => db.events.where("sessionId").equals(sessionId).sortBy("createdAt"), [sessionId]);
  const draftsQuery = useLiveQuery(() => db.drafts.where("sessionId").equals(sessionId).toArray(), [sessionId]);
  const events = useMemo(() => eventsQuery ?? [], [eventsQuery]);
  const drafts = useMemo(() => draftsQuery ?? [], [draftsQuery]);
  const session = useLiveQuery(() => db.sessions.get(sessionId), [sessionId]);
  /**
   * False while IndexedDB is still answering. An undefined query result means
   * "loading", not "no progress yet" — rendering or writing state before this
   * settles used to flash everything back to "not started" on every mount,
   * and could clobber a recorded layer status with a fresh "in-progress".
   */
  const hydrated = eventsQuery !== undefined && draftsQuery !== undefined;
  const progress = useMemo(() => projectProgress(guide, events), [guide, events]);

  const flushOutbox = useCallback(async () => {
    if (!remoteSessionId) return;
    const queued = await db.syncOutbox.where("remoteSessionId").equals(remoteSessionId).sortBy("createdAt");
    if (queued.length === 0) return;
    try {
      await appendHostedReviewEvents(remoteSessionId, queued.map((item) => item.event));
      await db.syncOutbox.bulkDelete(queued.map((item) => item.id));
    } catch {
      // IndexedDB remains authoritative while offline; the next event or online signal retries.
    }
  }, [remoteSessionId]);

  useEffect(() => {
    if (!remoteSessionId) return;
    void (async () => {
      const optimisticEvents = await db.events.where("sessionId").equals(sessionId).toArray();
      const syncable = optimisticEvents.filter((event) => event.type !== "replay-completed");
      if (syncable.length > 0) {
        await db.syncOutbox.bulkPut(syncable.map((event) => ({
          id: event.id,
          localSessionId: sessionId,
          remoteSessionId,
          event,
          createdAt: event.createdAt,
        })));
      }
      await flushOutbox();
    })();
    const onOnline = () => void flushOutbox();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [flushOutbox, remoteSessionId, sessionId]);

  const addEvent = useCallback(
    async (event: ReviewEventInput) => {
      const created = makeReviewEvent(event, { sessionId, guideHeadSha: guide.source.headSha });
      if (remoteSessionId && created.type !== "replay-completed") {
        await db.transaction("rw", [db.events, db.syncOutbox], async () => {
          await db.events.add(created);
          await db.syncOutbox.put({
            id: created.id,
            localSessionId: sessionId,
            remoteSessionId,
            event: created,
            createdAt: created.createdAt,
          });
        });
        void flushOutbox();
        return;
      }
      await db.events.add(created);
    },
    [flushOutbox, guide.source.headSha, remoteSessionId, sessionId],
  );

  const setLayerStatus = useCallback(
    (layerId: string, status: LayerStatus) => addEvent({ type: "layer-status", layerId, status }),
    [addEvent],
  );
  const setRegionStatus = useCallback(
    (regionId: string, status: RegionStatus) =>
      addEvent({ type: "region-status", regionId, status }),
    [addEvent],
  );
  const setFocusStopVisited = useCallback(
    (stopId: string) => addEvent({ type: "focus-stop-visited", stopId }),
    [addEvent],
  );
  const saveFeedback = useCallback(
    (layerId: string, itemId: string | undefined, message: string) =>
      addEvent({ type: "classification-feedback", layerId, itemId, message }),
    [addEvent],
  );
  const completeReplay = useCallback(
    async (durationMs: number, verdict: ReviewVerdict, summary: string) => {
      await addEvent({
        type: "replay-completed",
        durationMs,
        verdict,
        summary: summary.trim() || undefined,
      });
      await db.sessions.update(sessionId, {
        reviewVerdict: verdict,
        outcomeSummary: summary,
        completedAt: new Date().toISOString(),
      });
    },
    [addEvent, sessionId],
  );
  const saveOutcomeDraft = useCallback(
    (reviewVerdict: ReviewVerdict | undefined, outcomeSummary: string) =>
      db.sessions.update(sessionId, { reviewVerdict, outcomeSummary }),
    [sessionId],
  );
  const setActiveLayer = useCallback(
    async (layerId: string, activeFocusId?: string) => {
      await db.sessions.update(sessionId, {
        activeLayerId: layerId,
        activeFocusId,
        lastOpenedAt: new Date().toISOString(),
      });
    },
    [sessionId],
  );
  const saveLayerViewState = useCallback(
    async (layerId: string, state: Omit<ReviewLayerViewState, "updatedAt">) => {
      await db.transaction("rw", db.sessions, async () => {
        const current = await db.sessions.get(sessionId);
        if (!current) return;
        await db.sessions.update(sessionId, {
          layerViewStates: {
            ...current.layerViewStates,
            [layerId]: { ...state, updatedAt: new Date().toISOString() },
          },
          lastOpenedAt: new Date().toISOString(),
        });
      });
    },
    [sessionId],
  );
  const saveDraft = useCallback(
    async (itemId: string, body: string, anchor?: DraftAnchor, kind?: DraftCommentKind) => {
      const id = `${sessionId}:${itemId}`;
      const existing = await db.drafts.get(id);
      const draft: DraftComment = {
        id,
        sessionId,
        itemId,
        anchor: anchor ?? existing?.anchor,
        kind: kind ?? existing?.kind ?? "note",
        body,
        updatedAt: new Date().toISOString(),
      };
      await db.drafts.put(draft);
    },
    [sessionId],
  );
  const deleteDraft = useCallback(
    (itemId: string) => db.drafts.delete(`${sessionId}:${itemId}`),
    [sessionId],
  );

  // A stable object identity keeps `review`-dependent callbacks and memos in
  // the workspace from being recreated on every render.
  return useMemo(() => ({
    session,
    events,
    drafts,
    progress,
    hydrated,
    setLayerStatus,
    setRegionStatus,
    setFocusStopVisited,
    saveFeedback,
    completeReplay,
    saveOutcomeDraft,
    setActiveLayer,
    saveLayerViewState,
    saveDraft,
    deleteDraft,
  }), [
    session,
    events,
    drafts,
    progress,
    hydrated,
    setLayerStatus,
    setRegionStatus,
    setFocusStopVisited,
    saveFeedback,
    completeReplay,
    saveOutcomeDraft,
    setActiveLayer,
    saveLayerViewState,
    saveDraft,
    deleteDraft,
  ]);
}
